import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams, useLocation } from 'react-router-dom'
import { Text, useBoolean } from '@chakra-ui/react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

import { contactPreferenceWithRecaptcha } from 'services/api'
import { IResponseContactPreference } from 'services/IApi'
import { buttonVariant, statusReferredType } from 'components/informativeLayer/InformativeLayer.d'
import { isSuccessStatus } from 'utils/functions/validations'
import { InformativeLayer, useInformativeLayerImage, useInformativeLayerParagraph, useInformativeLayerTitle } from 'components/informativeLayer'

enum statusCodeReferred {
  LIMIT_EXCEEDED = 400,
  NOT_EXIT = 404,
  EXPIRED = 409,
  ERROR = 500
}

const ReferredImprovePlan: React.FC = () => {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const token: string = searchParams.get('token')
  const [isLoading, setIsLoading] = useBoolean(true)
  const [statusRefered, setStatusRefered] = useState<statusReferredType>(location?.state || 'LOADING')

  const { executeRecaptcha } = useGoogleReCaptcha()

  const title = useInformativeLayerTitle(statusRefered)
  const paragraph = useInformativeLayerParagraph(statusRefered)
  const image = useInformativeLayerImage(statusRefered)

  const handleReCaptchaVerify = useCallback(async (action: string) => {
    if (!executeRecaptcha) {
      console.info('Execute recaptcha not yet available')
      return ''
    }
    const token = await executeRecaptcha(action)
    return token
  }, [executeRecaptcha])

  const action = () => {
    if (statusRefered === 'EXPIRED') {
      window.location.href = 'https://interbanking.com.ar/ventas.html'
      return
    }
    setIsLoading.on()
    setStatusRefered('LOADING')
    contactPreference(token)
  }

  const defButton = useCallback((): { textButton: string | null; variantButton: buttonVariant | null } => {
    if (statusRefered === 'ERROR') {
      return { textButton: 'Reintentar', variantButton: 'link' }
    } else if (statusRefered === 'EXPIRED') {
      return { textButton: 'Solicitar una mejora', variantButton: 'link' }
    }
    return { textButton: null, variantButton: null }
  }, [statusRefered])

  const contactPreference = async (token: string) => {
    try {
      if (!executeRecaptcha) {
        console.info('Execute recaptcha not yet available')
        return
      }
      const recaptchaToken: string = await handleReCaptchaVerify('referredValidate')
      if (recaptchaToken) {
        const response: IResponseContactPreference = await contactPreferenceWithRecaptcha(token, recaptchaToken)
        setIsLoading.off()
        if (isSuccessStatus(response?.status)) {
          setStatusRefered('EXIT')
          return
        } else if (response?.status === statusCodeReferred.NOT_EXIT) {
          setStatusRefered('NOT_EXIT')
          return
        } else if (response?.status === statusCodeReferred.EXPIRED) {
          setStatusRefered('EXPIRED')
          return
        } else if (response?.status === statusCodeReferred.ERROR) {
          setStatusRefered('ERROR')
          return
        } else if (response?.status === statusCodeReferred.LIMIT_EXCEEDED) {
          setStatusRefered('LIMIT_EXCEEDED')
          return
        }
      }
      setStatusRefered('ERROR')
    } catch (error) {
      console.error('Error during reCAPTCHA execution:')
      setStatusRefered('ERROR')
    }
  }

  useEffect(() => {
    if (!executeRecaptcha && !location?.state) {
      return
    }
    if (token) {
      contactPreference(token)
    }
  }, [executeRecaptcha])

  return (
    <div>
      <InformativeLayer
        data-testid="informativeLayer"
        isLoading={location?.state ? false : isLoading}
        title={
          <Text
            data-testid="title"
            textStyle="h4Bold"
            fontWeight="700"
            color="#3B414E"
            textAlign="center"
          >
            {title}
          </Text>
        }
        paragraph={
          <Text
            data-testid="paragraph"
            textStyle="bodyLg"
            fontWeight="400"
            height={22}
            color="#292D36"
            textAlign="center"
          >
            {paragraph}
          </Text>
        }
        image={image}
        action={action}
        textButton={defButton().textButton}
        variantButton={defButton().variantButton}
      />
    </div>
  )
}

export default ReferredImprovePlan
